import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import CommonModal from '../Modal/Modal'
import Input from '../Input/Input'
import Button from '../Button/Button'
import { bookingInfoSchema, BookingInfoFormData } from './booking-info.schema'
import style from './booking-info.module.scss'
import PhoneInput from '../PhoneInput/PhoneInput'
import { useState } from 'react'

export interface BookingInfoModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: BookingInfoFormData) => void
  fleetName?: string
}

export const BookingInfoModal = ({
  isOpen,
  onClose,
  onConfirm,
  fleetName,
}: BookingInfoModalProps) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<BookingInfoFormData>({
    resolver: yupResolver(bookingInfoSchema),
    defaultValues: {
      bookingForSomeoneElse: 'myself',
      passengerCount: 1,
      luggage: '',
    },
  })

  const [guestPhoneWithoutCountryCode, setGuestPhoneWithoutCountryCode] = useState('')

  const bookingFor = watch('bookingForSomeoneElse')
  const showGuestFields = bookingFor === 'someoneelse'

  const onSubmit = (data: BookingInfoFormData) => {
    onConfirm(data)
    onClose()
  }

  return (
    <CommonModal
      open={isOpen}
      onCloseModal={onClose}
      title={`Complete Your Booking${fleetName ? ` - ${fleetName}` : ''}`}
      modalWidth="600px"
    >
      <form onSubmit={handleSubmit(onSubmit)} className={style.form}>
        {/* Trip Details Section */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Trip Details</h3>

          <Input
            register={register}
            name="passengerCount"
            label="Number of Passengers"
            type="number"
            min="1"
            errorMessage={errors.passengerCount?.message}
            outsideLabel={true}
            required={true}
          />

          <Input
            register={register}
            name="flightNumber"
            label="Flight Number"
            placeholder="Eg: BA123"
            errorMessage={errors.flightNumber?.message}
            outsideLabel={true}
          />

          <Input
            register={register}
            name="pickupSign"
            label="Pickup Sign"
            placeholder="Name to display on pickup sign"
            errorMessage={errors.pickupSign?.message}
            outsideLabel={true}
          />

          <Input
            register={register}
            name="luggage"
            label="Luggage"
            placeholder="e.g., 2 large suitcases, 1 carry-on"
            errorMessage={errors.luggage?.message}
            outsideLabel={true}
          />

          <div className={style.formGroup}>
            <label>Additional Notes</label>
            <textarea
              {...register('notes')}
              className={style.textarea}
              placeholder="Any special requests or instructions"
              rows={3}
            />
            {errors.notes && <span className={style.error}>{errors.notes.message}</span>}
          </div>
        </div>

        {/* Booking For Section */}
        <div className={style.section}>
          <h3 className={style.sectionTitle}>Who is this booking for?</h3>

          <div className={style.radioGroup}>
            <label className={style.radioLabel}>
              <input type="radio" value="myself" {...register('bookingForSomeoneElse')} />
              <span>Myself</span>
            </label>

            <label className={style.radioLabel}>
              <input type="radio" value="someoneelse" {...register('bookingForSomeoneElse')} />
              <span>Someone Else</span>
            </label>
          </div>

          {/* Guest Information - Conditional */}
          {showGuestFields && (
            <div className={style.guestFields}>
              <PhoneInput
                name="guestPhoneNumber"
                label="Guest Phone Number"
                placeholder="Enter Guest Phone Number"
                value={guestPhoneWithoutCountryCode}
                errorMessage={errors.guestPhoneNumber?.message}
                onChange={(value, fullPhoneNumber) => {
                  setGuestPhoneWithoutCountryCode(value)
                  setValue('guestPhoneNumber', fullPhoneNumber)
                }}
                required={true}
              />

              <Input
                register={register}
                name="guestFullName"
                label="Guest Full Name"
                placeholder="Full name of guest"
                errorMessage={errors.guestFullName?.message}
                outsideLabel={true}
              />

              <Input
                register={register}
                name="guestEmail"
                label="Guest Email"
                type="email"
                placeholder="guest@example.com"
                errorMessage={errors.guestEmail?.message}
                outsideLabel={true}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={style.actions}>
          <Button label="Cancel" onClick={onClose} type="button" buttonType="secondary" />
          <Button label="Continue to Payment" type="submit" />
        </div>
      </form>
    </CommonModal>
  )
}
